import { useEffect, useState } from "react";
import { Alert, Button, Card, CardBody, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import { getInstallationByIdentifier } from "../../services/installationService";
import BackToSalesButton from "./wizard/BackToSalesButton";
import WizardCard from "./wizard/WizardCard";

/**
 * Tab content for the Store Detail page that lets CSM/tech users
 * read and update the installation associated with a store.
 *
 * The full UI (steps, forms, validation, side-effects) will be built
 * incrementally; this component is the entry point referenced from
 * StoreDetailView so the rest of the page stays small.
 */
export default function InstallationWizard({ store }) {
  const [installation, setInstallation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);

  const location = store?.location ?? null;
  const storeName = store?.name ?? "";

  useEffect(() => {
    if (!location) {
      setIsLoading(false);
      setErrorStatus("missing-location");
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setErrorStatus(null);
    setInstallation(null);

    async function fetchInstallation() {
      try {
        const { data } = await getInstallationByIdentifier(location);
        if (!cancelled) {
          setInstallation(data);
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        setErrorStatus(status === 404 ? "not-found" : "unknown");
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }
    fetchInstallation();

    return () => {
      cancelled = true;
    };
  }, [location]);

  if (isLoading) {
    return (
      <Card>
        <CardBody className="text-center">
          <Spinner color="primary" />
        </CardBody>
      </Card>
    );
  }

  if (errorStatus || !installation) {
    const createHref = location
      ? `/installations/new?store_name=${encodeURIComponent(storeName)}&location=${encodeURIComponent(location)}`
      : "/installations/new";

    const message =
      errorStatus === "missing-location"
        ? "Cannot load installation: this store has no location identifier."
        : errorStatus === "not-found"
        ? `No installation is linked to store ${location ? `"${location}"` : "this store"} yet.`
        : "Failed to load the installation for this store.";

    return (
      <Card>
        <CardBody className="d-flex flex-column gap-2">
          <Alert color={errorStatus === "unknown" ? "danger" : "warning"} className="mb-0">
            <strong>Installation Wizard unavailable.</strong> {message}
          </Alert>
          {errorStatus === "not-found" && (
            <div>
              <Button tag={Link} to={createHref} size="sm" color="primary">
                Create installation
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Alert
        color={installation.back_to_sales_reason ? "warning" : "danger"}
        className="mb-3 py-2 d-flex align-items-center justify-content-between gap-2"
      >
        <span>
          {installation.back_to_sales_reason ? (
            <>
              <strong>Installation blocked.</strong> Reason: {installation.back_to_sales_reason.label}
            </>
          ) : (
            <>
              🚧 <strong>Work in progress — do not use yet.</strong> The Installation Wizard is under active
              development; feel free to browse, but please don&apos;t rely on it to onboard a real installation.
            </>
          )}
        </span>
        <BackToSalesButton installation={installation} onUpdated={setInstallation} />
      </Alert>
      <WizardCard installation={installation} />
    </>
  );
}
